import { createBrowserRouter, type RouteObject } from 'react-router';

import { RequireAuth } from '@/features/auth/guards/RequireAuth';

import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { RouteFallback } from './layouts/RouteFallback';
import { ROUTES } from './routes';

/**
 * Real URLs, real history, real code splitting.
 *
 * This is the single biggest thing being fixed relative to WalletFront, where
 * navigation was `useState('currentPage')` inside a 1758-line `App.jsx`: no
 * URLs, no deep links, no browser back button, and one bundle containing all 75
 * screens. Every route below is a separate `import()`, so the login screen no
 * longer ships the card module.
 *
 * Adding a screen means adding one object here — not editing two god-files.
 *
 * The route table is exported separately from the browser router so tests can
 * mount the same tree with an in-memory history.
 */
export const routes: RouteObject[] = [
  {
    element: <AuthLayout />,
    HydrateFallback: RouteFallback,
    children: [
      {
        path: ROUTES.welcome,
        lazy: async () => {
          const { default: Component } = await import('@/pages/auth/WelcomePage');
          return { Component };
        }
      },
      {
        path: ROUTES.login,
        lazy: async () => {
          const { default: Component } = await import('@/pages/auth/LoginPage');
          return { Component };
        }
      },
      {
        path: ROUTES.register,
        lazy: async () => {
          const { default: Component } = await import('@/pages/auth/RegisterPage');
          return { Component };
        }
      },
      {
        path: ROUTES.confirmEmail,
        lazy: async () => {
          const { default: Component } = await import('@/pages/auth/ConfirmEmailPage');
          return { Component };
        }
      },
      {
        path: ROUTES.forgotPassword,
        lazy: async () => {
          const { default: Component } = await import('@/pages/auth/ForgotPasswordPage');
          return { Component };
        }
      },
      {
        path: ROUTES.resetPassword,
        lazy: async () => {
          const { default: Component } = await import('@/pages/auth/ResetPasswordPage');
          return { Component };
        }
      }
    ]
  },
  {
    /*
     * THE GATE IS THE PARENT OF THE WHOLE APPLICATION SHELL.
     *
     * Not a wrapper inside `AppLayout` and not a per-route element: it renders
     * ABOVE the layout, so an unauthenticated visitor never gets the tab bar,
     * the gradient panel or a mounted screen — there is nothing to flash. It
     * also means a route added below is protected by default; making one public
     * requires moving it into the `AuthLayout` branch above, which is visible in
     * a diff.
     */
    element: <RequireAuth />,
    HydrateFallback: RouteFallback,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            lazy: async () => {
              const { default: Component } = await import('@/pages/HomePage');
              return { Component };
            }
          },
          {
            path: ROUTES.wallet,
            lazy: async () => {
              const { default: Component } = await import('@/pages/WalletPage');
              return { Component };
            }
          },
          {
            path: ROUTES.activity,
            lazy: async () => {
              const { default: Component } = await import('@/pages/activity/ActivityDetailPage');
              return { Component };
            }
          },
          {
            path: ROUTES.deposit,
            lazy: async () => {
              const { default: Component } = await import('@/pages/deposit/DepositMethodPage');
              return { Component };
            }
          },
          {
            path: ROUTES.depositCrypto,
            lazy: async () => {
              const { default: Component } = await import('@/pages/deposit/DepositCryptoPage');
              return { Component };
            }
          },
          {
            path: ROUTES.depositReceipt,
            lazy: async () => {
              const { default: Component } = await import('@/pages/deposit/DepositReceiptPage');
              return { Component };
            }
          },
          {
            path: ROUTES.shareId,
            lazy: async () => {
              const { default: Component } = await import('@/pages/deposit/ShareIdPage');
              return { Component };
            }
          },
          {
            path: ROUTES.withdraw,
            lazy: async () => {
              const { default: Component } = await import('@/pages/withdraw/WithdrawMethodPage');
              return { Component };
            }
          },
          {
            path: ROUTES.withdrawCrypto,
            lazy: async () => {
              const { default: Component } = await import('@/pages/withdraw/WithdrawCryptoPage');
              return { Component };
            }
          },
          {
            path: ROUTES.withdrawCard,
            lazy: async () => {
              const { default: Component } = await import('@/pages/withdraw/WithdrawCardPage');
              return { Component };
            }
          },
          {
            path: ROUTES.transfer,
            lazy: async () => {
              const { default: Component } = await import('@/pages/transfer/TransferPage');
              return { Component };
            }
          },
          {
            path: ROUTES.cards,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/CardsPage');
              return { Component };
            }
          },
          {
            // Before `/cards/:cardId`, so "new" is never read as a card id.
            path: ROUTES.cardsNew,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/AddCardPage');
              return { Component };
            }
          },
          {
            path: ROUTES.card,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/CardDetailPage');
              return { Component };
            }
          },
          {
            path: ROUTES.cardTopUp,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/TopUpCardPage');
              return { Component };
            }
          },
          {
            path: ROUTES.cardInfo,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/CardInfoPage');
              return { Component };
            }
          },
          {
            path: ROUTES.cardOtp,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/CardOtpPage');
              return { Component };
            }
          },
          {
            path: ROUTES.cardClose,
            lazy: async () => {
              const { default: Component } = await import('@/pages/cards/CloseCardPage');
              return { Component };
            }
          },
          {
            path: ROUTES.payouts,
            lazy: async () => {
              const { default: Component } = await import('@/pages/PayoutsPage');
              return { Component };
            }
          },
          {
            path: ROUTES.profile,
            lazy: async () => {
              const { default: Component } = await import('@/pages/ProfilePage');
              return { Component };
            }
          },
          {
            path: ROUTES.profileSecurity,
            lazy: async () => {
              const { default: Component } = await import('@/pages/profile/AccountSafetyPage');
              return { Component };
            }
          },
          {
            path: ROUTES.profileContacts,
            lazy: async () => {
              const { default: Component } = await import('@/pages/profile/ContactsPage');
              return { Component };
            }
          },
          {
            path: ROUTES.profileCards,
            lazy: async () => {
              const { default: Component } = await import('@/pages/profile/SavedCardsPage');
              return { Component };
            }
          },
          {
            path: '*',
            lazy: async () => {
              const { default: Component } = await import('@/pages/NotFoundPage');
              return { Component };
            }
          }
        ]
      }
    ]
  }
];

export const router = createBrowserRouter(routes);
